import React, { useMemo, useState } from 'react';

import { isValidEmail, Select } from '@aline/common';
import type { AutocompleteChangeReason, AutocompleteProps } from '@mui/material';
import { Autocomplete, InputBase } from '@mui/material';
import clsx from 'clsx';
import { uniqBy } from 'lodash';
import { matchSorter } from 'match-sorter';
import type { AvatarInfo } from 'src/components';
import { AlineAutocompleteInput, AlineAvatar, UserAutoCompleteOption } from 'src/components';
import { toAvatarPlaceholder } from 'src/components/AlineAvatar';
import { Apollo } from 'src/gql/generated';
import { always, getEntityDisplayDetails, userComparator } from 'src/helpers';
import { useGetOrgMembers } from 'src/hooks';
import { themeV5 } from 'src/theme';
import type { OrgSubset, UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

export const newUserEmailAvatarColor = themeV5.appColors.iconsType;

export enum GroupKey {
  Contacts = 'Contacts',
  Orgs = 'Orgs',
  Roles = 'Roles',
  Users = 'Users',
}

/**
 * A role is a plain label the caller turns into a new placeholder. There is no catalog of
 * existing roles to pick from: an existing placeholder is a workflow collaborator, chosen by id.
 */
interface NewRoleOption {
  __typename: 'NewRole';
  group: GroupKey.Roles;
  roleName: string;
}
type RoleWithGroupOption = NewRoleOption;

export type NewUserWithGroupOption = Omit<ExistingUserWithGroupOption, 'id'>;
interface ExistingUserWithGroupOption extends UserSubset {
  /** Contacts are users too — the group only decides which section they land in. */
  group: GroupKey.Contacts | GroupKey.Users;
  id: UUID;
}
export type UserWithGroupOption = ExistingUserWithGroupOption | NewUserWithGroupOption;

interface OrgWithGroupOption extends OrgSubset {
  group: GroupKey.Orgs;
  id: UUID;
}
type AccountWithGroupOption = UserWithGroupOption | OrgWithGroupOption;
export type UserRoleOption = UserWithGroupOption | RoleWithGroupOption;
export type AccountRoleOption = AccountWithGroupOption | RoleWithGroupOption;

/** Which sections of the option list a caller wants offered. */
export type IncludeAccountRoleOptions =
  | 'roles'
  | 'users'
  | 'users-contacts'
  | 'users-orgs'
  | 'users-roles'
  | 'users-roles-orgs';

type CustomAutocompleteProps = AutocompleteProps<AccountRoleOption, false, false, false>;
type OnOpen = CustomAutocompleteProps['onOpen'];
type OnClose = CustomAutocompleteProps['onClose'];
type OnInputChange = CustomAutocompleteProps['onInputChange'];
type FilterOptions = CustomAutocompleteProps['filterOptions'];
export type AccountsRolesAutocompleteValue = AccountRoleOption | null;

const useStyles = makeStyles()(theme => ({
  input: {
    '&:focus-within': {
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.appColors.primary}`,
    },
    '&:hover': {
      '&:focus-within': {
        backgroundColor: theme.palette.common.white,
        cursor: 'text',
      },
      backgroundColor: theme.appColors.hoverBackground,
      cursor: 'pointer',
    },
    border: `1px solid transparent`,
    borderRadius: 3,
    boxSizing: 'border-box',
    display: 'flex',
    padding: theme.spacing(0.75),
  },
  inputBase: {
    '&:hover': {
      '&:focus': {
        cursor: 'text',
      },
      cursor: 'pointer',
    },
    padding: theme.spacing(1),
  },
  inputBaseRoot: {
    '&:focus-within': {
      cursor: 'text',
    },
    cursor: 'pointer',
  },
}));

interface AccountsRolesAutoCompleteProps {
  onChange: (
    event: React.ChangeEvent<unknown>,
    value: AccountsRolesAutocompleteValue,
    reason: AutocompleteChangeReason
  ) => void;
  value: AccountsRolesAutocompleteValue;
  autoSelect?: boolean;
  avatarSize?: 'default' | 'small';
  blurOnSelect?: CustomAutocompleteProps['blurOnSelect'];
  className?: string;
  clearInputOnBlur?: boolean;
  clearOnBlur?: boolean;
  dataCy?: string;
  disabled?: boolean;
  documentId?: UUID;
  filterOrgsPred?: (org: OrgSubset) => boolean;
  filterUsersPred?: (user: UserSubset) => boolean;
  fullWidth?: boolean;
  includeOptions?: IncludeAccountRoleOptions;
  placeholder?: string;
  suggestInviteEmail?: boolean;
  suggestNewRole?: boolean;
  variant?: 'default' | 'avatar';
  workflowId?: UUID;
}

export function AccountsRolesAutoComplete(
  props: AccountsRolesAutoCompleteProps
): JSX.Element | null {
  const {
    autoSelect,
    blurOnSelect = true,
    clearInputOnBlur = false,
    className,
    disabled,
    filterOrgsPred = always,
    filterUsersPred = always,
    fullWidth = true,
    includeOptions = 'users',
    suggestInviteEmail = false,
    suggestNewRole = false,
    onChange,
    placeholder,
    documentId,
    workflowId,
    value,
    dataCy = 'doc-users-autocomplete',
    variant = 'default',
    avatarSize = 'default',
    clearOnBlur = variant === 'avatar',
  } = props;

  const { classes } = useStyles();
  const [highlightedOption, setHighlightedOption] = useState<AccountRoleOption | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const rolesIncluded =
    includeOptions === 'roles' ||
    includeOptions === 'users-roles' ||
    includeOptions === 'users-roles-orgs';
  const usersIncluded =
    includeOptions === 'users' ||
    includeOptions === 'users-contacts' ||
    includeOptions === 'users-roles' ||
    includeOptions === 'users-roles-orgs';
  const contactsIncluded = includeOptions === 'users-contacts';

  const {
    data: accountsData,
    loading: accountsLoading,
    refetch: refetchAccounts,
  } = Apollo.useGetAccountsByQuery({
    variables: { input: { documentId, workflowId } },
  });
  const {
    loading: contactsLoading,
    orgContacts,
    refetch: refetchContacts,
  } = useGetOrgMembers({
    skip: !contactsIncluded,
    variables: { filter: Apollo.OrgMemberFilter.Active },
  });
  const loading = accountsLoading || contactsLoading;
  const groupItems =
    includeOptions === 'users-contacts' ||
    includeOptions === 'users-roles' ||
    includeOptions === 'users-orgs' ||
    includeOptions === 'users-roles-orgs';
  const isAvatarVariant = variant === 'avatar';
  const options = useMemo((): AccountRoleOption[] => {
    return buildAccountRoleOptions({
      contacts: orgContacts.filter(filterUsersPred),
      includeOptions,
      orgs: (accountsData?.accounts.orgs ?? []).filter(filterOrgsPred),
      users: (accountsData?.accounts.users ?? []).filter(filterUsersPred),
    });
  }, [
    accountsData?.accounts.orgs,
    accountsData?.accounts.users,
    filterOrgsPred,
    filterUsersPred,
    includeOptions,
    orgContacts,
  ]);

  const handleOpen: OnOpen = async () => {
    setOpen(true);

    switch (includeOptions) {
      case 'users':
      case 'users-orgs':
      case 'users-roles':
      case 'users-roles-orgs':
        await refetchAccounts();
        break;
      case 'users-contacts':
        await Promise.all([refetchAccounts(), refetchContacts()]);
        break;
      case 'roles':
        break;
    }
  };

  const handleClose: OnClose = () => {
    setOpen(false);
  };

  const handleInputChange: OnInputChange = (__, inputValue) => {
    setInputValue(inputValue);
  };

  const filterOptionsAndSuggestIfNeeded: FilterOptions = (options, filterOptionsState) => {
    const userOptions = options.filter(
      (option): option is UserWithGroupOption => option.__typename === 'User'
    );
    const orgOptions = options.filter(
      (option): option is OrgWithGroupOption => option.__typename === 'Org'
    );

    // Order of keys matters and sets the search ranking.
    const userOptionKeys: (keyof UserWithGroupOption)[] = [
      'displayName',
      'firstName',
      'email',
      'lastName',
    ];
    /**
     * Contacts are users, so they have to be match-sorted as their own section: a single
     * ranked list would interleave the two groups, and MUI repeats a group header every
     * time the group changes.
     */
    const sortedUserOptions = matchSorter<AccountWithGroupOption>(
      userOptions.filter(option => option.group !== GroupKey.Contacts),
      inputValue,
      { keys: userOptionKeys }
    );
    const sortedContactOptions = matchSorter<AccountWithGroupOption>(
      userOptions.filter(option => option.group === GroupKey.Contacts),
      inputValue,
      { keys: userOptionKeys }
    );
    const orgOptionKeys: (keyof OrgWithGroupOption)[] = ['name'];
    const sortedOrgOptions = matchSorter<OrgWithGroupOption>(orgOptions, inputValue, {
      keys: orgOptionKeys,
    });

    const userAlreadyExists = options.some(
      option =>
        option.__typename === 'User' &&
        option.email.toLowerCase() === filterOptionsState.inputValue.toLowerCase()
    );

    const newRoleName = filterOptionsState.inputValue.trim();
    const newSuggestedRole =
      rolesIncluded && suggestNewRole && newRoleName
        ? [
            {
              __typename: 'NewRole',
              group: GroupKey.Roles,
              roleName: newRoleName,
            } satisfies NewRoleOption,
          ]
        : [];

    const newSuggestedEmail =
      usersIncluded && suggestInviteEmail && !userAlreadyExists
        ? [
            {
              __typename: 'User',
              avatarColor: newUserEmailAvatarColor,
              displayName: filterOptionsState.inputValue.trim(),
              email: filterOptionsState.inputValue.trim(),
              firstName: '',
              group: GroupKey.Users,
              lastName: '',
            } satisfies NewUserWithGroupOption,
          ]
        : [];

    // A typed or pasted external email leads, so Enter picks it instead of an internal match.
    const leadingEmail = isValidEmail(filterOptionsState.inputValue.trim())
      ? newSuggestedEmail
      : [];
    const trailingEmail = leadingEmail.length ? [] : newSuggestedEmail;

    return filterOptionsState.inputValue
      ? [
          ...leadingEmail,
          ...newSuggestedRole,
          ...trailingEmail,
          ...sortedUserOptions,
          ...sortedContactOptions,
          ...sortedOrgOptions,
        ]
      : [...sortedOrgOptions, ...sortedUserOptions, ...sortedContactOptions];
  };

  function getPlaceholder(): string {
    if (placeholder) return placeholder;

    switch (includeOptions) {
      case 'users':
        return 'Search users...';
      case 'roles':
        return 'Search roles...';
      case 'users-contacts':
        return 'Search users and contacts...';
      case 'users-roles':
        return 'Search users and roles...';
      case 'users-orgs':
      case 'users-roles-orgs':
        return 'Search accounts and roles...';
    }
  }

  return (
    <Autocomplete<AccountRoleOption, false, false, false>
      openOnFocus
      selectOnFocus
      autoHighlight={suggestInviteEmail}
      autoSelect={autoSelect}
      blurOnSelect={blurOnSelect}
      className={className}
      clearOnBlur={clearOnBlur}
      data-cy={dataCy}
      disabled={disabled}
      filterOptions={filterOptionsAndSuggestIfNeeded}
      fullWidth={fullWidth}
      getOptionDisabled={option => getIsNewUserOption(option) && !isValidEmail(option.email)}
      getOptionKey={(option): string => {
        switch (option.__typename) {
          case 'User':
            return 'id' in option ? option.id : option.email;
          case 'NewRole':
            return `NewRole:${option.roleName}`;
          case 'Org':
            return option.id;
        }
      }}
      getOptionLabel={(option): string => {
        switch (option.__typename) {
          case 'User':
            return option.displayName;
          case 'NewRole':
            return option.roleName;
          case 'Org':
            return option.name;
        }
      }}
      groupBy={groupItems ? option => option.group : undefined}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value): boolean => {
        return 'id' in option && 'id' in value && option.id === value.id;
      }}
      loading={loading}
      open={open}
      options={options}
      popupIcon={isAvatarVariant ? <NoDropdownIcon /> : undefined}
      renderInput={params => {
        const { slotProps, ...rest } = params;
        const isDefaultAvatar = avatarSize === 'default';
        const placeholder = getPlaceholder();
        const { avatarColor, avatarInfo } = getAccountRoleOptionDisplay(value);

        switch (variant) {
          case 'avatar':
            return (
              <div ref={slotProps.input.ref} className={classes.input}>
                <InputBase
                  classes={{
                    input: classes.inputBase,
                    root: clsx(classes.inputBaseRoot, slotProps.input.className),
                  }}
                  endAdornment={
                    React.isValidElement(slotProps.input.endAdornment)
                      ? React.cloneElement(slotProps.input.endAdornment, {
                          ['data-cy']: Select.components.accountsRolesAutocomplete.clearXButton,
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } as any)
                      : undefined
                  }
                  inputProps={slotProps.htmlInput}
                  placeholder={!value ? 'Unassigned' : placeholder}
                  startAdornment={
                    <AlineAvatar
                      avatarInfo={avatarInfo}
                      styles={{ backgroundColor: avatarColor, size: isDefaultAvatar ? 'md' : 'sm' }}
                    />
                  }
                  {...rest}
                />
              </div>
            );
          default:
            return <AlineAutocompleteInput placeholder={placeholder} {...params} />;
        }
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <li key={key} {...optionProps}>
            <UserAutoCompleteOption
              highlightedOptionId={
                highlightedOption && 'id' in highlightedOption ? highlightedOption.id : undefined
              }
              option={option}
            />
          </li>
        );
      }}
      slotProps={{
        listbox: {
          'data-cy': 'doc-users-autocomplete-options',
        },
      }}
      value={value}
      onBlur={() => clearInputOnBlur && setInputValue('')}
      onChange={onChange}
      onClose={handleClose}
      onHighlightChange={(_, option) => setHighlightedOption(option)}
      onInputChange={handleInputChange}
      onOpen={handleOpen}
    />
  );
}

interface BuildAccountRoleOptionsArgs {
  contacts: Omit<ExistingUserWithGroupOption, 'group'>[];
  includeOptions: IncludeAccountRoleOptions;
  orgs: Omit<OrgWithGroupOption, 'group'>[];
  users: Omit<ExistingUserWithGroupOption, 'group'>[];
}

/** Assembles the sections `includeOptions` asks for, each sorted and grouped for the listbox. */
export function buildAccountRoleOptions({
  contacts,
  includeOptions,
  orgs,
  users,
}: BuildAccountRoleOptionsArgs): AccountRoleOption[] {
  /**
   * The same account can arrive more than once (e.g. as an org member and a
   * permission holder); the listbox renders every option, so dedupe by id.
   */
  const dedupedUsers = uniqBy(users, user => user.id);
  const sortedOrgOptions = orgsToSortedOrgOptions(orgs);
  const sortedUserOptions = usersToSortedUserOptions(dedupedUsers);

  switch (includeOptions) {
    case 'users':
      return sortedUserOptions;
    case 'roles':
      return [];
    case 'users-contacts':
      return [
        ...sortedUserOptions,
        ...getContactOptionsForAccountRoles({ contacts, users: dedupedUsers }),
      ];
    case 'users-orgs':
      return [...sortedOrgOptions, ...sortedUserOptions];
    case 'users-roles':
      return sortedUserOptions;
    case 'users-roles-orgs':
      return [...sortedOrgOptions, ...sortedUserOptions];
  }
}

/**
 * Contacts are people outside the org, so an org member is never one. A contact can still
 * arrive as a user (e.g. holding a permission on the document being assigned) — keep the
 * user entry and drop the duplicate contact.
 */
export function getContactOptionsForAccountRoles({
  contacts,
  users,
}: {
  contacts: Omit<ExistingUserWithGroupOption, 'group'>[];
  users: Pick<ExistingUserWithGroupOption, 'id'>[];
}): ExistingUserWithGroupOption[] {
  const userIds = new Set(users.map(user => user.id));

  return contacts
    .filter(contact => !userIds.has(contact.id))
    .map((contact): ExistingUserWithGroupOption => ({ ...contact, group: GroupKey.Contacts }))
    .sort(userComparator);
}

function usersToSortedUserOptions<T extends Omit<UserWithGroupOption, 'group'>>(
  users: T[]
): UserWithGroupOption[] {
  return users
    .map(
      (user): UserWithGroupOption => ({
        ...user,
        group: GroupKey.Users,
      })
    )
    .sort(userComparator);
}

function orgsToSortedOrgOptions<T extends Omit<OrgWithGroupOption, 'group'>>(
  orgs: T[]
): OrgWithGroupOption[] {
  return orgs
    .map(
      (org): OrgWithGroupOption => ({
        ...org,
        group: GroupKey.Orgs,
      })
    )
    .sort((a, b) => a.name.localeCompare(b.name));
}

function NoDropdownIcon(): JSX.Element | null {
  return null;
}

export function getIsUserOption(
  value: AccountsRolesAutocompleteValue
): value is UserWithGroupOption {
  return value?.__typename === 'User';
}

export function getIsExistingUserOption(
  value: AccountsRolesAutocompleteValue
): value is ExistingUserWithGroupOption {
  return value?.__typename === 'User' && 'id' in value;
}

export function getIsNewUserOption(
  value: AccountsRolesAutocompleteValue
): value is NewUserWithGroupOption {
  return getIsUserOption(value) && !('id' in value);
}

export function getIsNewRoleOption(value: AccountsRolesAutocompleteValue): value is NewRoleOption {
  return value?.__typename === 'NewRole';
}

/** A new role has no account behind it, so it is shown by its name. */
export function getAccountRoleOptionDisplay(value: AccountsRolesAutocompleteValue): {
  avatarInfo: AvatarInfo;
  primary: string;
  avatarColor?: string;
  secondary?: string;
} {
  if (getIsNewRoleOption(value))
    return {
      avatarColor: newUserEmailAvatarColor,
      avatarInfo: toAvatarPlaceholder(value.roleName, null),
      primary: value.roleName,
    };

  return { ...getEntityDisplayDetails(value), avatarInfo: value };
}
